import type { Centre, MainCentre } from '@appTypes/auth';
import type { Session } from '@appTypes/shell';
import { create } from 'zustand';

interface AppShellStore {
  mainCentres: MainCentre[];
  centres: Centre[];
  sessions: Session[];
  mainCentreId: string;
  /** A centre id, or ALL_CENTRES for an admin's read-only rollup. */
  centreId: string;
  sessionId: string;
  unreadCount: number;
  shellReady: boolean;

  setMainCentreId: (id: string) => void;
  setCentreId: (id: string) => void;
  setSessionId: (id: string) => void;
  setMainCentres: (mainCentres: MainCentre[]) => void;
  setCentres: (centres: Centre[]) => void;
  setSessions: (sessions: Session[]) => void;
  setShellData: (payload: {
    mainCentres: MainCentre[];
    centres: Centre[];
    sessions: Session[];
    mainCentreId: string;
    centreId: string;
    sessionId: string;
  }) => void;
  setUnreadCount: (n: number) => void;
  setShellReady: () => void;
  resetShell: () => void;
}

export const useAppShellStore = create<AppShellStore>()((set) => ({
  mainCentres: [],
  centres: [],
  sessions: [],
  mainCentreId: localStorage.getItem('mainCentreId') ?? '',
  centreId: localStorage.getItem('centreId') ?? '',
  sessionId: localStorage.getItem('sessionId') ?? '',
  unreadCount: 0,
  shellReady: false,

  setMainCentreId: (id) => {
    localStorage.setItem('mainCentreId', id);
    set({ mainCentreId: id });
  },
  setCentreId: (id) => {
    localStorage.setItem('centreId', id);
    set({ centreId: id });
  },
  setSessionId: (id) => {
    localStorage.setItem('sessionId', id);
    set({ sessionId: id });
  },
  // Refresh one list only (e.g. on a `centres:changed` / `main-centres:changed`
  // realtime event) without touching the current selection — unlike setShellData,
  // which is for the full initial-bootstrap payload.
  setMainCentres: (mainCentres) => set({ mainCentres }),
  setCentres: (centres) => set({ centres }),
  setSessions: (sessions) => set({ sessions }),
  setShellData: ({ mainCentres, centres, sessions, mainCentreId, centreId, sessionId }) => {
    localStorage.setItem('mainCentreId', mainCentreId);
    localStorage.setItem('centreId', centreId);
    localStorage.setItem('sessionId', sessionId);
    set({ mainCentres, centres, sessions, mainCentreId, centreId, sessionId });
  },
  setUnreadCount: (n) => set({ unreadCount: n }),
  setShellReady: () => set({ shellReady: true }),
  resetShell: () => set({ mainCentres: [], centres: [], sessions: [], unreadCount: 0, shellReady: false }),
}));
